if (!customElements.get('product-tabs-carousel')) {
  class ProductTabsCarousel extends HTMLElement {
    constructor() {
      super();
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.cleanups = [];
    }

    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));
      this.navs = Array.from(this.querySelectorAll('[data-tab-nav]'));
      this.status = this.querySelector('[data-ptc-status]');

      this.tabs.forEach((tab, index) => {
        this.listen(tab, 'click', () => this.select(index, false));
        this.listen(tab, 'keydown', (event) => this.onTabKeydown(event, index));
      });

      this.listen(this, 'click', (event) => this.onClick(event));
      this.listen(this, 'keydown', this.onKeydown);
      this.listen(this, 'focusin', (event) => this.onFocusIn(event));

      this.initSizeGroups();

      const ready = window.customElements.whenDefined('swiper-component');
      ready.then(() => requestAnimationFrame(() => this.bindSwipers()));

      if (this.hasAttribute('data-rv')) this.initRecentlyViewed();
    }

    disconnectedCallback() {
      this.cleanups.forEach((cleanup) => cleanup());
      this.cleanups = [];
      document.removeEventListener('click', this.onDocumentClick);
      if (this.rvAbort) {
        this.rvAbort.abort();
        this.rvAbort = null;
      }
    }

    /* ---- "Recently viewed" source (product_source: recently_viewed) ----
       History = product handles in localStorage, newest first. The current product is recorded
       after reading, so it never lists itself; cards come back server-rendered from the
       section's own search-context branch (one request, shared card snippet). */

    readHistory() {
      try {
        const raw = window.localStorage.getItem(ProductTabsCarousel.RV_KEY);
        const list = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(list)) return [];
        return list.filter((handle) => typeof handle === 'string' && /^[a-z0-9][a-z0-9_-]*$/i.test(handle));
      } catch (error) {
        return [];
      }
    }

    writeHistory(list) {
      try {
        window.localStorage.setItem(ProductTabsCarousel.RV_KEY, JSON.stringify(list));
      } catch (error) {
        /* storage unavailable (private mode / blocked): the rail simply stays hidden */
      }
    }

    initRecentlyViewed() {
      const current = this.dataset.rvCurrent || '';
      const limit = Math.min(16, parseInt(this.dataset.rvLimit, 10) || 12);
      const history = this.readHistory();

      let handles = history;
      if (current && this.hasAttribute('data-rv-exclude-current')) {
        handles = handles.filter((handle) => handle !== current);
      }
      handles = handles.slice(0, limit);

      if (current && history[0] !== current) {
        const next = [current].concat(history.filter((handle) => handle !== current));
        this.writeHistory(next.slice(0, ProductTabsCarousel.RV_MAX));
      }

      if (!handles.length) return;

      const query = `ptc-rv:${this.dataset.rvFlags || 'x'}:${handles.join(',')}`;
      const base = this.dataset.rvUrl || '/search';
      const url = `${base}?q=${encodeURIComponent(query)}&type=product&section_id=product-tabs-carousel`;

      this.rvAbort = new AbortController();
      fetch(url, { signal: this.rvAbort.signal, credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
        .then((html) => this.renderRecentlyViewed(html))
        .catch((error) => {
          if (error && error.name === 'AbortError') return;
          console.error('product-tabs-carousel: recently viewed', error);
        });
    }

    renderRecentlyViewed(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const slides = Array.from(doc.querySelectorAll('[data-ptc-rv-results] > .swiper-slide'));
      const track = this.querySelector('[data-ptc-rv-track]');
      if (!track || !slides.length) return;

      track.replaceChildren(...slides.map((slide) => document.importNode(slide, true)));
      track.parentElement.style.setProperty('--swiper-slide-count', slides.length);
      this.classList.remove('is-rv-empty');
      this.initSizeGroups();

      window.customElements.whenDefined('swiper-component').then(() =>
        requestAnimationFrame(() => {
          const panel = this.panels[0];
          const swiper = this.getSwiper(panel);
          if (!swiper) return;
          swiper.update();
          swiper.slideTo(0, 0);
          if (swiper.navigation && swiper.navigation.update) swiper.navigation.update();
          if (swiper.scrollbar && swiper.scrollbar.updateSize) swiper.scrollbar.updateSize();
          this.bindSwipers();
          this.syncLock(panel);
          this.dispatchEvent(new CustomEvent('ptc:recently-viewed-rendered', { bubbles: true }));
        })
      );
    }

    listen(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    get motionReduced() {
      if (window.Theme && Theme.config && typeof Theme.config.motionReduced === 'boolean') {
        return Theme.config.motionReduced;
      }
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    getSwiper(panel) {
      const el = panel && panel.querySelector('swiper-component');
      return el ? el.swiper : null;
    }

    bindSwipers() {
      this.panels.forEach((panel) => {
        const swiper = this.getSwiper(panel);
        if (!swiper || swiper.__ptcBound) return;
        swiper.__ptcBound = true;
        swiper.on('sliderMove', () => this.classList.add('is-dragging'));
        swiper.on('touchEnd', () => {
          window.setTimeout(() => this.classList.remove('is-dragging'), 50);
        });
        swiper.on('slideChange', () => this.closeQuick());
        swiper.on('resize', () => this.syncLock(panel));
        this.syncLock(panel);
      });
    }

    /* The host swiper-component flags "not enough slides to scroll" as .swiper--locked
       (Swiper itself never locks here: slidesOffsetAfter inflates its virtual size). */
    syncLock(panel) {
      const el = panel.querySelector('swiper-component');
      const swiper = el && el.swiper;
      if (!swiper) return;
      if (typeof el.updateLock === 'function') el.updateLock();
      const locked = el.classList.contains('swiper--locked');
      const nav = this.navs.find((n) => n.dataset.tabNav === panel.dataset.tabPanel);
      if (nav) nav.classList.toggle('is-locked', locked);
      swiper.allowTouchMove = !locked;
      if (locked && swiper.activeIndex !== 0) swiper.slideTo(0, 0);
    }

    /* ---- Tabs ---- */

    onTabKeydown(event, index) {
      const last = this.tabs.length - 1;
      let next = null;
      if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
      if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = last;
      if (next === null) return;
      event.preventDefault();
      this.select(next, true);
    }

    select(index, moveFocus) {
      const current = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      if (moveFocus) this.tabs[index].focus();
      if (index === current) return;

      this.closeQuick();

      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
        tab.classList.toggle('is-active', active);
      });

      this.navs.forEach((nav) => {
        nav.hidden = Number(nav.dataset.tabNav) !== index;
      });

      const incoming = this.panels.find((panel) => Number(panel.dataset.tabPanel) === index);
      this.panels.forEach((panel) => {
        if (panel !== incoming) panel.hidden = true;
      });
      if (!incoming) return;
      incoming.hidden = false;

      const swiper = this.getSwiper(incoming);
      if (swiper) {
        swiper.update();
        swiper.slideTo(0, 0);
        if (swiper.scrollbar && swiper.scrollbar.updateSize) swiper.scrollbar.updateSize();
        if (swiper.navigation && swiper.navigation.update) swiper.navigation.update();
        this.syncLock(incoming);
      }

      this.animatePanel(incoming);
    }

    animatePanel(panel) {
      if (!window.Theme || !Theme.motion || this.motionReduced) return;
      Theme.motion.animate(
        panel,
        { opacity: [0, 1], transform: ['translateY(8px)', 'translateY(0)'] },
        { duration: 0.3, easing: [0.25, 0.1, 0.25, 1] }
      );
    }

    /* ---- Keyboard focus keeps off-track slides reachable ---- */

    onFocusIn(event) {
      const slide = event.target.closest('.swiper-slide');
      if (!slide) return;
      const panel = slide.closest('[data-tab-panel]');
      const swiper = this.getSwiper(panel);
      if (!swiper) return;
      const index = swiper.slides.indexOf(slide);
      if (index < 0) return;
      const rect = slide.getBoundingClientRect();
      const box = swiper.el.getBoundingClientRect();
      if (rect.left < box.left - 1 || rect.right > box.right + 1) {
        swiper.el.scrollLeft = 0;
        swiper.slideTo(index);
      }
    }

    /* ---- Quick size select ---- */

    initSizeGroups() {
      this.querySelectorAll('[data-ptc-sizes]').forEach((group) => {
        const chips = Array.from(group.querySelectorAll('[data-variant-id]'));
        const first = chips.find((chip) => chip.getAttribute('aria-disabled') !== 'true') || chips[0];
        chips.forEach((chip) => chip.setAttribute('tabindex', chip === first ? '0' : '-1'));
      });
    }

    onClick(event) {
      const bag = event.target.closest('[data-ptc-bag]');
      if (bag) {
        event.preventDefault();
        this.toggleQuick(bag.closest('[data-ptc-card]'), event.detail === 0);
        return;
      }
      const chip = event.target.closest('[data-variant-id]');
      if (chip && this.contains(chip)) {
        event.preventDefault();
        this.addToCart(chip);
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        const open = this.querySelector('.ptc-card.is-quick-open');
        if (open) {
          event.stopPropagation();
          this.closeQuick(true);
        }
        return;
      }

      const chip = event.target.closest && event.target.closest('[data-variant-id]');
      if (!chip) return;
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      const chips = Array.from(chip.parentElement.querySelectorAll('[data-variant-id]'));
      const index = chips.indexOf(chip);
      let next = index;
      if (event.key === 'ArrowRight') next = Math.min(chips.length - 1, index + 1);
      if (event.key === 'ArrowLeft') next = Math.max(0, index - 1);
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = chips.length - 1;
      event.preventDefault();
      chips.forEach((c, i) => c.setAttribute('tabindex', i === next ? '0' : '-1'));
      chips[next].focus();
    }

    toggleQuick(card, fromKeyboard) {
      if (!card) return;
      const isOpen = card.classList.contains('is-quick-open');
      this.closeQuick();
      if (isOpen) return;
      card.classList.add('is-quick-open');
      const bag = card.querySelector('[data-ptc-bag]');
      if (bag) bag.setAttribute('aria-expanded', 'true');
      this.openCard = card;
      const target = card.querySelector('[data-variant-id][tabindex="0"]');
      if (target && fromKeyboard) requestAnimationFrame(() => target.focus());
      document.addEventListener('click', this.onDocumentClick);
    }

    closeQuick(restoreFocus) {
      const card = this.openCard;
      this.openCard = null;
      document.removeEventListener('click', this.onDocumentClick);
      if (!card) return;
      card.classList.remove('is-quick-open');
      const bag = card.querySelector('[data-ptc-bag]');
      if (bag) {
        bag.setAttribute('aria-expanded', 'false');
        if (restoreFocus) bag.focus();
      }
    }

    onDocumentClick(event) {
      if (this.openCard && !this.openCard.querySelector('.ptc-card__quick').contains(event.target)) {
        this.closeQuick();
      }
    }

    announce(message) {
      if (!this.status || !message) return;
      this.status.textContent = '';
      window.setTimeout(() => {
        this.status.textContent = message;
      }, 50);
    }

    addToCart(chip) {
      if (chip.getAttribute('aria-disabled') === 'true' || chip.classList.contains('is-loading')) return;
      const variantId = chip.dataset.variantId;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(chip);
      }

      const config =
        typeof window.fetchConfig === 'function'
          ? window.fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/javascript' } };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      chip.classList.add('is-loading');
      chip.setAttribute('aria-busy', 'true');

      fetch(addUrl, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.announce(response.description || response.message || this.dataset.errorMessage);
            return;
          }
          if (!cart) {
            window.location = cartUrl;
            return;
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-tabs-carousel',
              productVariantId: variantId,
              cartData: response,
            });
          }
          this.closeQuick();
          cart.renderContents(response);
          this.announce(this.dataset.addedMessage);
        })
        .catch((error) => {
          console.error(error);
          this.announce(this.dataset.errorMessage);
        })
        .finally(() => {
          chip.classList.remove('is-loading');
          chip.removeAttribute('aria-busy');
        });
    }
  }

  ProductTabsCarousel.RV_KEY = 'blanca:recently-viewed';
  ProductTabsCarousel.RV_MAX = 20;

  customElements.define('product-tabs-carousel', ProductTabsCarousel);
}
